import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import { Command as CommandIcon, Globe, Pencil, Search, X } from "lucide-react";
import { cn } from "@web/lib/cn";
import { useAccountLookup } from "@web/lib/accounts";
import type { ThreadListView } from "@web/lib/endpoints";
import { viewOf, type ListSearch } from "@web/inbox/listSearch";
import { NotificationsBell } from "@web/notify/Bell";
import { AccountChip, accountColor, accountLabel } from "@web/ui/AccountChip";
import { Kbd } from "@web/ui/Kbd";
import { ClaudeMenu } from "./ClaudeMenu";
import { runCommand, useCommand } from "./commands";
import { isMacPlatform } from "./keymap";

const VIEW_TABS: { view: ThreadListView; label: string }[] = [
  { view: "inbox", label: "Inbox" },
  { view: "unread", label: "Unread" },
  { view: "starred", label: "Starred" },
  { view: "all", label: "All mail" },
];

/**
 * The search field. On a phone it hides behind an icon in the top bar (`onOpen` shows it) so the
 * bar never squeezes it; `onClose` hides it again once the search is cleared.
 */
function SearchBox({
  search,
  onOpen,
  onClose,
}: {
  search: ListSearch;
  onOpen: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  // Keyed by the active query (see TopBar), so a new search in the URL resets the text.
  const [text, setText] = useState(search.q ?? "");
  // Off by default: the cache answers at once; Gmail is asked only when the reader says so.
  const [gmail, setGmail] = useState(search.scope === "gmail");

  useCommand("search.focus", () => {
    // Show the field (phones) before focusing it, still inside the tap so the keyboard opens.
    flushSync(onOpen);
    inputRef.current?.focus();
    inputRef.current?.select();
  });

  const go = (q: string | undefined, scope = gmail) =>
    navigate({
      to: "/inbox",
      search: {
        q: q || undefined,
        account: search.account,
        scope: q && scope ? "gmail" : undefined,
      },
    });

  return (
    <form
      role="search"
      className="relative flex h-10 min-w-0 flex-1 items-center gap-2 md:max-w-lg"
      onSubmit={(e) => {
        e.preventDefault();
        void go(text.trim());
        inputRef.current?.blur();
      }}
    >
      <div className="relative flex min-w-0 flex-1 items-center">
        <Search aria-hidden className="pointer-events-none absolute left-3 size-4 text-muted" />
        <input
          ref={inputRef}
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            if (text) setText("");
            if (search.q) void go(undefined);
            inputRef.current?.blur();
            onClose();
          }}
          placeholder={gmail ? "Search all of Gmail" : "Search mail"}
          aria-label="Search mail"
          enterKeyHint="search"
          className={cn(
            "h-10 w-full rounded-lg border border-line bg-paper pr-10 pl-9 text-[15px] text-ink",
            "placeholder:text-faint [&::-webkit-search-cancel-button]:hidden",
          )}
        />
        <span className="pointer-events-none absolute right-3 hidden items-center sm:flex">
          {text ? null : <Kbd>/</Kbd>}
        </span>
        {text ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setText("");
              if (search.q) void go(undefined);
            }}
            className="absolute right-2 rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
      <label
        className={cn(
          "inline-flex h-10 shrink-0 cursor-pointer items-center gap-1 rounded-lg border px-1.5 text-xs select-none sm:gap-1.5 sm:px-2",
          gmail
            ? "border-line-strong bg-paper text-ink"
            : "border-transparent text-muted hover:text-ink",
        )}
        title="Search all of Gmail (asks Gmail itself for the account shown; results are cached here)"
      >
        <input
          type="checkbox"
          checked={gmail}
          onChange={(e) => {
            setGmail(e.target.checked);
            // A search already on screen switches scope at once.
            if (search.q) void go(search.q, e.target.checked);
          }}
          className="size-3.5 accent-[var(--accent)]"
          aria-label="Search all of Gmail"
          data-testid="search-gmail-toggle"
        />
        <Globe aria-hidden className="size-3.5" />
        <span className="hidden md:inline">All of Gmail</span>
      </label>
      <button
        type="button"
        aria-label="Close search"
        onClick={() => {
          setText("");
          if (search.q) void go(undefined);
          onClose();
        }}
        className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-sunk hover:text-ink md:hidden"
      >
        <X className="size-5" />
      </button>
    </form>
  );
}

function ListFilters({ search }: { search: ListSearch }) {
  const { list: accounts, colorIndex, label } = useAccountLookup();
  const view = viewOf(search);
  return (
    // Wraps rather than scrolls sideways, so nothing is cut off on a phone.
    <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line px-4 py-1.5">
      {search.q ? (
        <span className="text-sm text-muted">
          Results for <span className="text-ink">“{search.q}”</span>
          {search.scope === "gmail" ? " · all of Gmail" : ""}
        </span>
      ) : (
        <div
          role="tablist"
          aria-label="Which threads"
          className="flex items-center gap-1 whitespace-nowrap"
        >
          {VIEW_TABS.map((tab) => {
            const active = tab.view === view && !search.label;
            return (
              <Link
                key={tab.view}
                role="tab"
                aria-selected={active}
                to="/inbox"
                search={{
                  account: search.account,
                  view: tab.view === "inbox" ? undefined : tab.view,
                }}
                className={cn(
                  "rounded-md px-2.5 py-1 text-sm",
                  active
                    ? "bg-paper font-medium text-ink shadow-soft"
                    : "text-muted hover:text-ink",
                )}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}
      {accounts.length > 1 ? (
        <div
          className="flex flex-wrap items-center gap-1.5 whitespace-nowrap"
          aria-label="Accounts"
        >
          <span aria-hidden className="h-4 w-px bg-line" />
          <Link
            to="/inbox"
            search={{ view: search.view, label: search.label, q: search.q, scope: search.scope }}
            aria-pressed={!search.account}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs",
              !search.account
                ? "border-line-strong bg-paper text-ink"
                : "border-transparent text-muted hover:text-ink",
            )}
          >
            All accounts
          </Link>
          {accounts.map((a) => {
            const active = search.account === a.id;
            return (
              <Link
                key={a.id}
                to="/inbox"
                search={{
                  view: search.view,
                  // A user label belongs to one account; switching accounts drops it.
                  label: search.label && !/^[A-Z_]+$/.test(search.label) ? undefined : search.label,
                  q: search.q,
                  scope: search.scope,
                  account: active ? undefined : a.id,
                }}
                aria-pressed={active}
                title={a.email}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs",
                  active
                    ? "border-line-strong bg-paper text-ink"
                    : "border-transparent text-muted hover:text-ink",
                )}
              >
                <span
                  aria-hidden
                  className="size-1.5 rounded-full"
                  style={{ background: accountColor(colorIndex(a.id)) }}
                />
                {label(a.email) ?? accountLabel(a.email)}
              </Link>
            );
          })}
        </div>
      ) : accounts.length === 1 && accounts[0] ? (
        // One account needs no filter; a phone has no room to name it.
        <AccountChip
          email={accounts[0].email}
          colorIndex={0}
          full
          className="ml-auto hidden sm:inline-flex"
        />
      ) : null}
    </div>
  );
}

export function TopBar({
  pathname,
  search,
  signedIn,
}: {
  pathname: string;
  search: ListSearch;
  signedIn: boolean;
}) {
  const onList = pathname === "/inbox";
  // Phones show search behind an icon. It stays open while a search is on screen and closes
  // when you go to another screen.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const phoneSearch = openOn === pathname || (onList && !!search.q);
  return (
    <header className="shrink-0 bg-bg">
      <div className="flex h-14 items-center gap-2 border-b border-line px-4">
        <div className={cn("min-w-0 flex-1 md:flex md:max-w-lg", phoneSearch ? "flex" : "hidden")}>
          <SearchBox
            key={onList ? (search.q ?? "") : ""}
            search={onList ? search : {}}
            onOpen={() => setOpenOn(pathname)}
            onClose={() => setOpenOn(null)}
          />
        </div>
        <div
          className={cn(
            "min-w-0 flex-1 items-center gap-2 md:hidden",
            phoneSearch ? "hidden" : "flex",
          )}
        >
          <span className="font-serif text-lg font-semibold">MeeMail</span>
          <button
            type="button"
            onClick={() => runCommand("search.focus")}
            aria-label="Search mail"
            data-testid="topbar-search"
            className="ml-auto inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-sunk hover:text-ink"
          >
            <Search className="size-5" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => runCommand("compose.new")}
          className={cn(
            "ml-auto h-10 shrink-0 items-center gap-2 rounded-lg border border-line bg-paper px-3 text-sm text-muted hover:text-ink md:inline-flex",
            phoneSearch ? "hidden" : "inline-flex",
          )}
          aria-label="New message"
          title="New message (c)"
          data-testid="topbar-compose"
        >
          <Pencil className="size-4" />
          <span className="hidden md:inline">New message</span>
          <Kbd className="hidden md:inline-flex">C</Kbd>
        </button>
        {/* Phones list the same links under More. */}
        <ClaudeMenu pathname={pathname} className="hidden shrink-0 md:block" />
        <button
          type="button"
          onClick={() => runCommand("palette.open")}
          className="hidden h-10 items-center gap-2 rounded-lg border border-line bg-paper px-3 text-sm text-muted hover:text-ink sm:inline-flex"
          aria-label="Open command palette"
        >
          <CommandIcon className="size-4" />
          <span>Commands</span>
          <Kbd>{isMacPlatform() ? "⌘K" : "Ctrl K"}</Kbd>
        </button>
        <div className={cn("shrink-0 md:block", phoneSearch ? "hidden" : "block")}>
          <NotificationsBell enabled={signedIn} />
        </div>
      </div>
      {onList ? <ListFilters search={search} /> : null}
    </header>
  );
}
